import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { isCancel } from 'axios'
import dayjs from 'dayjs'
import { toastStore } from 'shared/ui'
import { type PageLayoutStore } from 'shared/layout'
import { delay, logger } from 'shared/lib'
import { DayjsFormats } from 'shared/lib/dayjs'
import { RequestCancelAxios } from 'shared/api/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { stringToNumber } from 'shared/lib/stringToNumber'
import { ConversationsApi } from 'entities/Conversation'
import {
  type IParamsCreateMessage,
  type IParamsMessagesGetContactsByIdTeamsById,
  type IParamsUpdateMessage,
  type IResponseMessage,
  type IResponseRecordTranscriptStatus,
  MessagesApi,
} from 'entities/Message'
import { ContactsApi } from 'entities/Contacts'
import { type Attachment } from 'entities/Attachment/model/Attachment'
import { resolveMediaSizes } from 'entities/Attachment'
import {
  type IParamsCreateSuggestAnswer,
  type IParamsCreateSummaryAnswer,
} from 'entities/AIAssistant'
import type { IResponseActivity } from 'entities/Activity/api/types'
import type { IResponseEventTyping } from 'entities/Typing/api/types'
import { Activity } from 'entities/Activity/model/Activity'
import { Message } from 'entities/Message/model/Message'
import { Typing } from 'entities/Typing/model/Typing'
import { type Integration } from 'entities/Integrations/model/Integration'
import { Viewing } from 'entities/Viewing/model/Viewing'
import type { IResponseEventViewing, IViewingStatus } from 'entities/Viewing'
import { type Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  getLocalContactTimeFormatted,
  getNextQuietHoursSendAt,
  isInQuietHoursTimeframe,
} from 'entities/QuietHours'
import { QuietHours } from 'entities/QuietHours/model'
import { QuietHoursClosureStore } from 'entities/QuietHours/store/quietHoursClosureStore'
import { CallingContentStore } from 'widgets/ConversationMessages/ui/CallingContent'
import {
  getScheduleText,
  type IScheduledData,
} from 'widgets/ConversationSchedule/store/conversationScheduleStore'
import { type CallModalStore } from 'widgets/CallModal'
import { CallContentStore } from 'widgets/MessageCard'
import {
  type IConversationMessagesStoreConfig,
  type IConversationMessagesVariant,
} from 'widgets/ConversationMessages/store/types'
import { SavedRepliesStore } from 'widgets/SavedReplies'
import { ConversationMessageFieldStore } from 'widgets/ConversationMessages/store/conversationMessageFieldStore'
import { ConversationMessageFieldAccessStore } from 'widgets/ConversationMessages/store/conversationMessageFieldAccessStore'
import { ExportConversationModalStore } from 'widgets/ExportConversationModal'
import { type SendFromStore } from 'widgets/SendFrom'
import { ChatbotExecutionStore } from 'widgets/ConversationMessages/ui/ConversationMessageFieldAlerts/ConversationChatbotAlert/store/ChatbotExecutionStore'
import { getSendMessageAlert } from 'widgets/ConversationMessages/helpers/messageAlert'

export type IUndoFieldSnapshot = {
  conversationId: number
  message: string
  attachments: Attachment[]
}

export class ConversationMessagesStore {
  private _callModalStore: CallModalStore | null = null

  private _current_page: number | null = null
  private _per_page = 20
  private _page = 1

  private _conversationId: number | null = null
  private _contactId: number | null = null
  private _inboxId: number | null = null
  private _numberId: number | null = null
  private _messageId: number | null = null

  private _itemsMap: Map<string | number, Message | Activity> = new Map()
  private _sendingMessagesIdsMap: Map<number | string, string | number> = new Map()
  private _pendingUndoSnapshots: Map<number, IUndoFieldSnapshot> = new Map()
  private _skipDraftLoadFor: Set<number> = new Set()
  private _scrollBottomTrigger = ''
  private _mediaLoadedTrigger = ''

  private _loading = true
  private _loadingConversation = false
  private _loadingConversations = false
  private _loadingPrevious = false
  private _loadingMessages = false
  private _loadingOverlay = false
  private _hasNext = true
  private _hasPrevious = false

  private _loadingBeforeId: string | number = 0
  private _isScheduleMessage = false
  private _isEditMessage = false
  private _handleReceiveNewScroll = true
  private _typingsMap: Map<number, Typing> = new Map()
  private _callingContentStore: CallingContentStore
  private _viewingsMap: Map<number, Viewing> = new Map()
  private _cancelRequest = new RequestCancelAxios()
  private _isHideTrialAlertNotForOwner = false
  private _isClickToUndo: boolean
  private _warning: string | null = null
  private _isModeNoteObj = {
    value: false,
  }

  private _loadingPreviousPromise: Promise<void> | null = null
  private _disposeIsClickToUndo: IReactionDisposer | null = null
  private _disposeConversation: IReactionDisposer | null = null
  private _disposeActiveTab: IReactionDisposer | null = null

  private _callContentStore: CallContentStore
  private _config: IConversationMessagesStoreConfig | null = null

  private _isActiveTab = true
  private _canSendViewing = true
  private _throttleViewingTime = 30000
  private _onViewingTimerId: NodeJS.Timeout | null = null
  private _savedRepliesStore: SavedRepliesStore
  private _conversationMessageFieldStore: ConversationMessageFieldStore
  private _conversationMessageFieldAccessStore: ConversationMessageFieldAccessStore
  private _isHideAvatars = false
  private _isBottomFixed = true
  private _messagesContentScrollHeight = 0
  private _cancelRequestGetConversation = new RequestCancelAxios()
  private _exportConversationModalStore = new ExportConversationModalStore()
  private _variant: IConversationMessagesVariant | null = null
  private _enableTrialAlertLimitConversations = false
  private _selectedSearch = ''
  private _hasSearchParams = false
  private _chatbotExecutionStore = new ChatbotExecutionStore()

  constructor(private _entities: IEntitiesStore, private _pageLayoutStore?: PageLayoutStore) {
    this._callingContentStore = new CallingContentStore(() => this.conversationId)
    this._callContentStore = new CallContentStore(this, this._entities)
    this._isClickToUndo = this._entities.userSettingsStore.isClickToUndo
    this._savedRepliesStore = new SavedRepliesStore(this._entities)
    this._conversationMessageFieldStore = new ConversationMessageFieldStore(this, this._entities)
    this._conversationMessageFieldAccessStore = new ConversationMessageFieldAccessStore(
      this,
      this._entities
    )
    makeAutoObservable(this)
  }

  get messageId() {
    return this._messageId
  }

  get loadingOverlay() {
    return this._loadingOverlay
  }

  get chatbotExecutionStore() {
    return this._chatbotExecutionStore
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  get exportConversationModalStore() {
    return this._exportConversationModalStore
  }

  get hasSearchParams() {
    return this._hasSearchParams
  }

  get selectedSearch() {
    return this._selectedSearch
  }

  get loadingConversations() {
    return this._loadingConversations
  }

  get isBottomFixed() {
    return this._isBottomFixed
  }

  get messagesContentScrollHeight() {
    return this._messagesContentScrollHeight
  }

  get isHideAvatars() {
    return this._isHideAvatars
  }

  get conversationMessageFieldStore() {
    return this._conversationMessageFieldStore
  }

  get conversationMessageFieldAccessStore() {
    return this._conversationMessageFieldAccessStore
  }

  setHasSearchParams = (value: boolean) => {
    this._hasSearchParams = value
  }

  setSelectedSearch = (value: string) => {
    this._selectedSearch = value
  }

  setLoadingConversations = (value: boolean) => {
    this._loadingConversations = value
  }

  initReactions = () => {
    this._conversationMessageFieldStore.initReactions()

    this.reactionIsClickToUndo()
    this.reactionConversation()
    this.reactionActiveTab()
    this._entities.integrationsStore.fetchIntegrations()
  }

  reactionConversation = () => {
    this._disposeConversation?.()
    this._disposeConversation = reaction(
      () => this.conversation,
      (conversation) => {
        if (!conversation) return null

        this._enableTrialAlertLimitConversations = conversation.id === 0

        if (conversation) {
          this.setInboxId(conversation.inbox_id)
          this.setContactId(conversation.contact_id)
          this.onViewing()
        } else {
          this.setLoadingConversations(false)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionActiveTab = () => {
    this._disposeActiveTab?.()
    this._disposeActiveTab = reaction(
      () => this._isActiveTab,
      (status) => {
        if (status && this._canSendViewing) {
          this.onViewing()
        }
      }
    )
  }

  reactionIsClickToUndo = () => {
    this._disposeIsClickToUndo?.()
    this._disposeIsClickToUndo = reaction(
      () => this._entities.userSettingsStore.isClickToUndo,
      (value) => {
        this._isClickToUndo = value
      }
    )
  }

  setConfig = (config: IConversationMessagesStoreConfig) => {
    this._callModalStore = config.callModalStore || null
    this._config = {
      ...this._config,
      ...config,
    }
  }

  setMessagesContentScrollHeight = (value: number) => {
    this._messagesContentScrollHeight = value
  }

  get config() {
    return this._config
  }

  get enableTrialAlertLimitConversations() {
    return this._enableTrialAlertLimitConversations
  }

  get savedRepliesStore() {
    return this._savedRepliesStore
  }

  get callContentStore() {
    return this._callContentStore
  }

  get inboxId() {
    return this._inboxId
  }

  get inbox() {
    return this._entities.inboxesStore.getItem(this._inboxId)
  }

  get number() {
    return this._entities.numbersStore.getItem(this._numberId)
  }

  get contactId() {
    return this._contactId
  }

  get contact() {
    if (!this._contactId) return null

    return this._entities.contactsStore.getItem(this._contactId)
  }

  get contactIds() {
    if (!this.conversation) return []

    return this.conversation.contactsIds
  }

  get contacts() {
    if (!this.conversation) return []

    return this.conversation.contactsIds.reduce<Contact[]>((state, id) => {
      const contact = this._entities.contactsStore.getItem(id)

      if (contact?.id) {
        state.push(contact)
      }

      return state
    }, [])
  }

  get integration(): Integration | null {
    if (!this.contact?.integration_key) return null

    // TODO: change key to salesmessage after back
    if (this.contact?.integration_key === 'salesmsg') return null

    return this._entities.integrationsStore.getIntegration(this.contact.integration_key)
  }

  get profileConnectedIntegrations() {
    return this._entities.integrationsStore.connectedIntegrations
  }

  get isClosed() {
    return this.conversation?.isClosed
  }

  get isRead() {
    return this.conversation?.isRead
  }

  get isUnread() {
    return this.conversation?.isUnread
  }

  get allMessages() {
    const messages: Array<Message> = []
    Array.from(this._itemsMap.values()).forEach((item) => {
      if (item instanceof Message) {
        messages.push(item)
      }
    })
    return messages
  }

  get sendingMessages() {
    return this.allMessages
      .filter((message) => message.sending)
      .filter((message) => message.conversation_id === this.conversationId)
  }

  get beforeId() {
    return this.items[0]?.id
  }

  get isGroup() {
    return this.conversation?.is_group
  }

  get items(): Array<Message | Activity> {
    return Array.from(this._itemsMap.values())
      .filter((message) => message instanceof Activity || !message.sending)
      .filter(
        (message) =>
          message instanceof Activity ||
          (message.type === 'call' ? message.record || message.body : true)
      )
      .sort((a, b) =>
        b.sortDateTime === a.sortDateTime
          ? Number(a.id) - Number(b.id)
          : a.sortDateTime - b.sortDateTime
      )
  }
  get scheduledItems() {
    return this.items.filter((message) => message instanceof Message && message.is_schedule)
  }

  get ordinaryItems() {
    return this.items.filter((message) => message instanceof Activity || !message.is_schedule)
  }

  get isEmpty(): boolean {
    return this.items.length === 0
  }

  get messages(): Array<Message> {
    const messages: Array<Message> = []
    this.items.forEach((item) => {
      if (item instanceof Message) {
        messages.push(item)
      }
    })
    return messages
  }

  get priority() {
    return this.conversation?.priority
  }

  get typings(): Typing[] {
    return Array.from(this._typingsMap.values())
  }

  get typingsCount() {
    return this.typings.length
  }

  get callingContentStore() {
    return this._callingContentStore
  }

  get viewings(): Viewing[] {
    return Array.from(this._viewingsMap.values())
  }

  get showDoubleOptInButton() {
    return !this.contact?.isOptedIn && this._entities.organizationStore.doubleOptInEnabled
  }

  get warningText() {
    return this._warning
  }

  onLoadMediaContent = () => {
    if (this.isBottomFixed) this.setScrollBottomTrigger()
    this.setMediaLoadedTrigger()
  }

  setWarningText = (text: string | null) => {
    this._warning = text
  }

  getContactTZ = () => {
    return this.contact?.timezone || ''
  }

  initCancelTokenSource = () => {
    this._cancelRequest.init()
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setHasPrevious = (value: boolean) => {
    this._hasPrevious = value
  }

  reset = () => {
    this.setLoading(true)
    this._hasPrevious = false
    this._resetLoadingState()
    this._page = 1
    this._loadingBeforeId = 0
    this.resetMessage()
    this.setScheduleMessage(false)
    this._isEditMessage = false
    this._typingsMap.clear()
    this._callingContentStore.clear()
    this._viewingsMap.clear()
    this._sendingMessagesIdsMap.clear()
    this._canSendViewing = true
    this._cancelRequestGetConversation.reset()
    this._loadingConversations = false
    this._current_page = null
    this._loadingOverlay = false

    this._conversationMessageFieldStore.dispose()
    if (this._onViewingTimerId) clearTimeout(this._onViewingTimerId)
  }

  resetReactions = () => {
    this._conversationMessageFieldStore.disposeReactions()

    this._disposeIsClickToUndo?.()
    this._disposeConversation?.()
    this._disposeActiveTab?.()
  }

  resetConversationId = () => {
    if (this._conversationId) {
      this.conversationStore.getItem(this._conversationId)?.flushDebounceOnCreateDraft?.()
    }
    this._conversationId = null
  }

  eventSegmentSendType = () => {
    if (!this._config?.eventSegmentSendType) return

    this._entities.eventLogStore.logEvent(
      'BigConvSlideout_Message_Sent',
      {
        event_id: 'bigconvslideout_message_sent',
        from: this._config.eventSegmentSendType,
        agencyId: this._entities.organizationStore.agency_id,
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  startSendProcess = async (
    message: IParamsCreateMessage,
    scheduledData?: IScheduledData,
    conversationId?: number,
    withUndo?: boolean,
    onSendSuccess?: () => void
  ) => {
    this.eventSegmentSendType()
    await this._config?.onStartSendMessage?.()

    if (this.conversation?.isNew) {
      this._config?.onStartSendProcess?.(
        message,
        scheduledData,
        conversationId,
        withUndo,
        onSendSuccess
      )
    } else {
      this.sendMessage({
        message,
        conversationId,
        onSendSuccess,
      })

      this._config?.onFinishSendMessage?.()
    }

    this.setScrollBottomTrigger()
  }

  takeFieldSnapshot = (conversationId: number): IUndoFieldSnapshot => ({
    conversationId,
    message: this.messageFieldStore.messageText,
    attachments: [...this.messageFieldStore.attachments],
  })

  private _applySnapshotToField = (snapshot: IUndoFieldSnapshot) => {
    this._skipDraftLoadFor.add(snapshot.conversationId)
    this._entities.conversationStore.deleteItemDraft(snapshot.conversationId)

    this.messageFieldStore.setMessageData({
      message: snapshot.message,
      attachments: snapshot.attachments,
      isReplace: true,
      isFocus: true,
    })
  }

  private _showRestoreConfirmation = (snapshot: IUndoFieldSnapshot) => {
    const id = 'undo_restore_confirmation'

    modalStore.addModal({
      type: ModalTypeList.WARNING,
      title: 'Discard your current draft?',
      id,
      desc: 'Restoring the undone message will overwrite the text and attachments you have in the input now.',
      primaryAction: {
        text: 'Restore message',
        onAction: () => {
          modalStore.removeModal(id)
          if (this.conversationId === snapshot.conversationId) {
            this._applySnapshotToField(snapshot)
          } else {
            this._pendingUndoSnapshots.set(snapshot.conversationId, snapshot)
          }
        },
      },
      secondaryAction: {
        text: 'Keep my draft',
        onAction: () => {
          modalStore.removeModal(id)
        },
      },
    })
  }

  restoreFieldFromUndo = (snapshot: IUndoFieldSnapshot) => {
    if (this.conversationId !== snapshot.conversationId) {
      this._pendingUndoSnapshots.set(snapshot.conversationId, snapshot)
      return
    }

    if (this.messageFieldStore.isEmptyDraft) {
      this._applySnapshotToField(snapshot)
      return
    }

    this._showRestoreConfirmation(snapshot)
  }

  showUndoProcess = (scheduledData?: IScheduledData, onSendSuccess?: () => void) => {
    const id = parseInt(nanoid().replace(/\D/g, ''), 10)
    const toastId = String(id)
    const timer = 5000
    const message = this._conversationMessageFieldStore.getMessageToSend(scheduledData)
    const snapshot = this.takeFieldSnapshot(this.conversationId)

    const client_id = nanoid()
    const undoPendingMessage: Message = new Message(
      {
        payload: {
          requestMessage: {
            media_url: message.media_url,
            body: message.message,
            send_at: new Date().toISOString(),
            sent_at: new Date().toISOString(),
            type: 'sms',
            sending: false,
            undoPending: true,
            conversation_id: this.conversationId,
          },
          client_id,
        },
      },
      this._entities
    )

    this._itemsMap.set(client_id, undoPendingMessage)
    if (this._handleReceiveNewScroll) this.setReceivedNewMessageTrigger()

    const timeoutId = setTimeout(() => {
      this.startSendProcess(
        message,
        scheduledData,
        undoPendingMessage.conversation_id,
        true,
        onSendSuccess
      )
      this._itemsMap.delete(client_id)
      toastStore.remove(toastId)
    }, timer)

    toastStore.add({
      type: 'info',
      id: toastId,
      title: 'Sending in',
      withCountdown: true,
      timer,
      action: {
        text: 'Undo',
        onAction: () => {
          toastStore.remove(toastId)
          clearTimeout(timeoutId)
          this._itemsMap.delete(client_id)
          this.restoreFieldFromUndo(snapshot)
        },
      },
      secondAction: {
        text: 'Instant send',
        onAction: () => {
          this.startSendProcess(
            message,
            scheduledData,
            undoPendingMessage.conversation_id,
            true,
            onSendSuccess
          )
          this._itemsMap.delete(client_id)
          toastStore.remove(toastId)
          clearTimeout(timeoutId)
        },
      },
    })
  }

  handleSend = async ({
    scheduledData,
    onSendSuccess,
  }: { scheduledData?: IScheduledData; onSendSuccess?: () => void } = {}) => {
    this._conversationMessageFieldStore.conversationMessageFieldDraftStore.handleResetDraft()

    const message: IParamsCreateMessage =
      this._conversationMessageFieldStore.getMessageToSend(scheduledData)
    const scheduleMessage = message.send_at
    const noteMessage = message.type === 'note'
    const undoMessage = this._isClickToUndo && !scheduleMessage && !noteMessage

    undoMessage && !this.isConversationNew
      ? this.showUndoProcess(scheduledData, onSendSuccess)
      : await this.startSendProcess(
          message,
          scheduledData,
          this.conversationId,
          undoMessage,
          onSendSuccess
        )
    this.messageFieldStore.reset()
  }

  onSendAction = async ({ scheduledData }: { scheduledData?: IScheduledData }) => {
    const disabled = this._config?.onCheckSendAction?.()

    if (disabled) return
    if (this.messageFieldStore.disablesSend) return

    if (this.isShowQuietHoursModal && !scheduledData) {
      this.showQuietHoursModal({ scheduledData })
      return
    }

    this.handleSend({ scheduledData })
  }

  onCloseEdit = () => {
    this._isEditMessage = false
    this.setScheduleMessage(false)
    this.messageFieldStore.setEditMessage(null)
  }

  onSaveEditMessageAction = () => {
    if (!this.messageFieldStore.editMessage) {
      return
    }

    this?.updateMessage({
      message: this.messageFieldStore?.paramsUpdateMessage,
      messageId: this.messageFieldStore.editMessage?.id,
    })
    this.onCloseEdit()
  }

  getParamsCreateSuggestAnswer = (): IParamsCreateSuggestAnswer => {
    return {
      messages: this.messages
        .filter(
          (message) =>
            (message.type === 'sms' || message.type === 'mms' || message.type === 'rcs') &&
            message.body
        )
        .slice(-20)
        .map((message) => ({
          message: message.body,
          first_name: message.first_name,
          direction: message.isOutbound ? 'outbound' : 'inbound',
        })) as IParamsCreateSuggestAnswer['messages'],
    }
  }

  getParamsCreateSummaryAnswer = (): IParamsCreateSummaryAnswer => {
    return {
      messages: this.messages
        .filter(
          (message) =>
            (message.type === 'sms' ||
              message.type === 'mms' ||
              message.type === 'note' ||
              message.type === 'rcs' ||
              (message.type === 'call' && message.transcriptions)) &&
            message.body
        )
        .slice(-20)
        .map((message) => ({
          message: message.body.slice(0, 1600),
          first_name: message.first_name || message.phone_number,
          direction: message.isOutbound ? 'outbound' : 'inbound',
        })) as IParamsCreateSummaryAnswer['messages'],
    }
  }

  setContactId = (id: number) => {
    this._contactId = id
  }

  setInboxId = (id: number) => {
    this._inboxId = id
  }

  setNumberId = (id: number) => {
    this._numberId = id
  }

  setConversationId = (conversationId: number, isLoading = true) => {
    if (this._conversationId === conversationId) return

    const conversation = this.conversationStore.getItem(conversationId)

    if (this._conversationId) {
      this.conversationStore.getItem(this._conversationId)?.flushDebounceOnCreateDraft?.()
    }

    this._conversationId = conversationId
    this._resetLoadingState()

    const pendingSnapshot = this._pendingUndoSnapshots.get(conversationId)
    if (pendingSnapshot) {
      this._pendingUndoSnapshots.delete(conversationId)
      this.restoreFieldFromUndo(pendingSnapshot)
    } else {
      this._conversationMessageFieldStore.conversationMessageFieldDraftStore.handleSetDraft()
    }

    if (conversation) {
      this.setNumberId(conversation.number_id)
      this.setInboxId(conversation.inbox_id)
      this.setContactId(conversation.contact_id)
    }

    if (isLoading) {
      this.loadData()
    }

    if (this.showDoubleOptInButton) {
      this.messageFieldStore.setDisabledMessageField(this.showDoubleOptInButton)
    }
  }

  setMessageId = (id: number) => {
    this._messageId = id
  }

  resetMessage = () => {
    this._itemsMap.clear()
  }

  loadConversation = async () => {
    try {
      runInAction(() => {
        this._loadingConversation = true
      })

      const id = Number(this.conversationId)

      if (!id) return

      this._cancelRequestGetConversation.init()

      const conversation = await this.conversationStore.getById({
        id,
        isModalError: true,
        isFetch: true,
        update: true,
        options: {
          cancelToken: this._cancelRequestGetConversation.token,
        },
      })

      if (conversation) {
        if (this._skipDraftLoadFor.has(conversation.id)) {
          this._skipDraftLoadFor.delete(conversation.id)
        } else {
          this.conversationStore.addItemDraft(conversation.origin.draft_message)
          this._conversationMessageFieldStore.conversationMessageFieldDraftStore.handleSetDraft()
        }
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingConversation = false
      })
    }
  }

  loadMessages = async (page?: number) => {
    const conversationId = this._conversationId

    try {
      if (!this.conversationId) {
        this.setLoading(false)
        return
      }
      if (this._loadingMessages) return
      runInAction(() => {
        this._loadingMessages = true
        this._page = page || 1
      })

      this.initCancelTokenSource()

      const response = await MessagesApi.getMessagesByConversationId(
        {
          conversationId: +this.conversationId,
          page: this._page,
          per_page: this._per_page,
          with_activity_logs: this.isShowActivity ? 1 : 0,
          message_id: this.messageIdQuery,
        },
        {
          cancelToken: this._cancelRequest.token,
        }
      )

      if (!this._current_page) {
        this._current_page = response.data.current_page
      }

      if (Math.abs(this._page - response.data.current_page) > 0) {
        this._page = response.data.current_page
      }

      if (
        this._entities.organizationStore.isActivityLoggingEnabled &&
        this._page === 1 &&
        this._contactId &&
        response?.data.data.length > 0
      ) {
        this._entities.organizationStore.handleTrackOrganizationActivity({
          event: 'phi.conversation.viewed',
          data: {
            contact_id: this._contactId,
            conversation_id: this.conversationId,
          },
        })
      }

      if (response?.data) {
        await resolveMediaSizes(response.data.data)

        this._applyForConversation(conversationId, () => {
          this.setItems(response.data.data)
          this._hasPrevious = response.data.total / this._per_page > response.data.current_page
          if (this._page === 1) this.setScrollBottomTrigger()
        })
      }

      this._applyForConversation(conversationId, () => {
        this._loadingMessages = false
        this.setLoading(false)
      })
    } catch (e) {
      logger.error(e)

      const isCancelled = isCancel(e) && !this.isConversationNew

      this._applyForConversation(conversationId, () => {
        this._loadingMessages = isCancelled
        this.setLoading(isCancelled)
      })
    }
  }

  loadMessagesContact = async ({ before }: { before?: number | string } = {}) => {
    const conversationId = this._conversationId

    try {
      const selectedContacts = this.contacts
      const selectedInboxId = this._inboxId
      let messages: IResponseMessage[] = []
      let hasPrevious = false

      if (this.loading) return
      if (!selectedContacts.length) return
      if (!selectedInboxId) return

      this.setLoading(true)

      if (selectedContacts.length === 1) {
        const contact = selectedContacts[0]
        const params: IParamsMessagesGetContactsByIdTeamsById = {
          grouped_response: true,
          limit: 20,
        }

        if (before) {
          params.before = before
        }

        this.initCancelTokenSource()

        const response = await MessagesApi.getMessagesContactsByIdTeamsById(
          contact.id,
          selectedInboxId,
          params,
          {
            cancelToken: this._cancelRequest.token,
          }
        )
        const data = response.data

        messages = data.messages
        hasPrevious = data.hasPrevious
      } else {
        const response = await MessagesApi.getMessagesContacts({
          contacts: selectedContacts.map((item) => item.id),
          teamId: selectedInboxId,
          limit: 20,
          before,
        })
        const data = response.data

        messages = data
        hasPrevious = data.length === 20
      }

      if (messages) {
        await resolveMediaSizes(messages)

        this._applyForConversation(conversationId, () => {
          this.setItems(messages)

          this._hasNext = false
          this._hasPrevious = hasPrevious

          if (!before) this.setScrollBottomTrigger()
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this._applyForConversation(conversationId, () => this.setLoading(false))
    }
  }

  loadData = async () => {
    if (this.isConversationNew) {
      this.initCancelTokenSource()
      this.setLoading(false)
    } else {
      await this.loadMessages()
    }
  }

  private _resetLoadingState = () => {
    this._loadingMessages = false
    this._loadingOverlay = false
    this._loadingPrevious = false
    this._loadingPreviousPromise = null
  }

  private _applyForConversation = (conversationId: number | null, apply: () => void) => {
    if (this._conversationId !== conversationId) return

    runInAction(apply)
  }

  private _loadPrevious = async (onSuccess?: () => void) => {
    const conversationId = this._conversationId

    runInAction(() => {
      this._loadingBeforeId = this.beforeId
      this._loadingPrevious = true
    })

    try {
      if (this.isConversationNew) {
        await this.loadMessagesContact({
          before: this._loadingBeforeId,
        })
      } else {
        await this.loadMessages(this._page + 1)
      }

      onSuccess?.()
    } catch (e) {
      logger.error(e)
    } finally {
      this._applyForConversation(conversationId, () => {
        this._loadingPrevious = false
        this._loadingPreviousPromise = null
      })
    }
  }

  loadPreviousMessages = (onSuccess?: () => void) => {
    if (!this.hasPrevious) return
    if (this._loadingPreviousPromise) return this._loadingPreviousPromise

    this._loadingPreviousPromise = this._loadPrevious(onSuccess)

    return this._loadingPreviousPromise
  }

  loadMessagesAllNext = async () => {
    if (!this._current_page) return
    if (this._current_page === 1) return

    const conversationId = this._conversationId

    runInAction(() => {
      this._loadingOverlay = true
    })

    try {
      this.initCancelTokenSource()

      const response = await MessagesApi.getMessagesByConversationId(
        {
          conversationId: +this.conversationId,
          page: 1,
          per_page: (this._current_page + 1) * this._per_page,
          with_activity_logs: this.isShowActivity ? 1 : 0,
        },
        {
          cancelToken: this._cancelRequest.token,
        }
      )

      this._current_page = null

      if (response?.data) {
        await resolveMediaSizes(response.data.data)

        this._applyForConversation(conversationId, () => {
          this.setItems(response.data.data)
          this._hasPrevious = response.data.total > this.allMessages.length
          if (this._page === 1) this.setScrollBottomTrigger()
        })
      }

      await delay(500)

      this._applyForConversation(conversationId, () => {
        this._loadingMessages = false
        this._loadingOverlay = false
        this.setLoading(false)
      })
    } catch (e) {
      logger.error(e)

      const isCancelled = isCancel(e) && !this.isConversationNew

      this._applyForConversation(conversationId, () => {
        this._loadingMessages = isCancelled
        this._loadingOverlay = isCancelled
        this.setLoading(isCancelled)
      })
    }
  }

  async sendMessage({
    message,
    conversationId,
    onSendSuccess,
  }: {
    message: IParamsCreateMessage
    conversationId?: number
    onSendSuccess?: () => void
  }) {
    const client_id = nanoid()
    const id = conversationId || this.conversationId
    const sendingMessage = new Message(
      {
        payload: {
          requestMessage: {
            media_url: message.media_url,
            body: message.message,
            send_at: message.send_at,
            type: message.type,
            conversation_id: id,
          },
          client_id,
        },
      },
      this._entities
    )

    runInAction(() => {
      this._itemsMap.set(client_id, sendingMessage)
    })

    if (this._handleReceiveNewScroll) this.setReceivedNewMessageTrigger()

    try {
      const payload: IParamsCreateMessage = {
        ...message,
        client_id,
      }

      if (!id) return

      const { data } = await MessagesApi.createMessage(id, payload)

      runInAction(() => {
        this._sendingMessagesIdsMap.set(data.id, client_id)
        this.messageFieldStore.clearSendAlert()
      })

      if (data) {
        const { data: updatedConversation } = await ConversationsApi.updateByIdRead(id)

        this.conversationStore.updateItem(updatedConversation)

        if (this.conversationStore.currentItemId === data.conversation_id) {
          await this.insertItems([{ ...data, client_id }])
        }

        try {
          onSendSuccess?.()
        } catch (e) {
          logger.error(e)
        }
      }
    } catch (error) {
      runInAction(() => {
        const alert = getSendMessageAlert(error)
        this.messageFieldStore.setSendAlert(alert)
        this._itemsMap.delete(client_id)
        logger.error(error)
      })
    }
  }

  async updateMessage({
    message,
    messageId,
  }: {
    message: IParamsUpdateMessage
    messageId: number
  }) {
    try {
      runInAction(() => {
        const clientId = this._sendingMessagesIdsMap.get(messageId) || messageId
        const oldMessage = this._itemsMap.get(clientId)
        if (oldMessage && oldMessage instanceof Message) {
          oldMessage.sending = true
          this._itemsMap.set(clientId, oldMessage)
        }
      })
      const { data } = await MessagesApi.updateMessage(messageId, message)
      if (data) {
        await this.insertItems([data])
        this.setScrollBottomTrigger()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  insertItems = async (items: Array<IResponseMessage | IResponseActivity>) => {
    const conversationId = this._conversationId

    await resolveMediaSizes(items)

    this._applyForConversation(conversationId, () => this.setItems(items))
  }

  setItems = (items: Array<IResponseMessage | IResponseActivity>) => {
    if (items.length === 1 && items[0].type === 'call' && this.isHideCalls) return

    items.forEach((message) => {
      if (message.item_type === 'message' && message.client_id) {
        this._sendingMessagesIdsMap.set(message.id, message.client_id)
      }
    })

    const iItems = items.map((item) => {
      if (item.item_type === 'activity_log') {
        return new Activity(
          {
            item: item,
          },
          this._entities
        )
      }
      const client_id = this._sendingMessagesIdsMap.get(item.id) || item.id
      return this.addItem(item, client_id)
    })

    let links: string[] = []

    try {
      iItems.forEach((item) => {
        if (item instanceof Message) {
          links = links.concat(item.links)
          this._itemsMap.set(item.client_id, item)
        } else {
          this._itemsMap.set(item.id, item)
        }
      })
    } catch (e) {
      logger.error(e)
    }

    if (this._handleReceiveNewScroll) {
      this.setReceivedNewMessageTrigger()
    }
    this._entities.attachmentStore.checkPreviousAttachment(links)
  }

  setHandleReceiveNewScroll = (value: boolean) => {
    this._handleReceiveNewScroll = value
  }

  updateTranscript = (item: IResponseMessage) => {
    const message = this._itemsMap.get(item.id)
    if (message instanceof Message) {
      message.setTranscript(item)
    }
  }

  updateTranscriptStatus = (id: number, status: string) => {
    const message = this._itemsMap.get(id)
    if (message instanceof Message) {
      message.setStatus(status)
    }
  }

  updateRecordTranscriptStatus = (id: number, status: IResponseRecordTranscriptStatus) => {
    const message = this._itemsMap.get(id)
    if (message instanceof Message) {
      message.setRecordTranscriptStatus(status)
    }
  }

  addItem = (item: IResponseMessage, client_id: string | number) => {
    const message = new Message(
      {
        message: { ...item, client_id },
      },
      this._entities
    )

    if (Array.isArray(item.pending_mentions)) {
      item.pending_mentions.forEach((item) => {
        this._entities.usersStore.addItem(item)
      })
    }

    return message
  }

  async retry(id: number | string) {
    try {
      const { data } = await MessagesApi.retry(id)
      await this.insertItems([data])
    } catch (e) {
      logger.error(e)
    }
  }

  async delete(id: number) {
    try {
      await MessagesApi.deleteMessage(id)
      const clientId = this._sendingMessagesIdsMap.get(id) || id
      this._itemsMap.delete(clientId)
      if (!this.isConversationNew) await this._conversationMessageFieldStore.handleRead()
    } catch (e) {
      logger.error(e)
    }
  }

  setScrollBottomTrigger = () => {
    this._scrollBottomTrigger = nanoid()
  }

  setMediaLoadedTrigger = () => {
    this._mediaLoadedTrigger = nanoid()
  }

  setReceivedNewMessageTrigger = () => {
    if (this.isBottomFixed) this.setScrollBottomTrigger()
  }

  setIsBottomFixed = (value: boolean) => {
    this._isBottomFixed = value
  }

  setScheduleMessage = (value: boolean) => {
    this._isScheduleMessage = value
  }

  setIsEditMessage = (isEditMessage: boolean) => {
    this._isEditMessage = isEditMessage
  }

  handleOnToggle = async (value: boolean, featureKey: string, settingsKey: string) => {
    try {
      runInAction(() => {
        this.reset()
      })

      await this._entities.userSettingsStore.updateSetting({
        value,
        featureKey,
        settingsKey,
      })
      this.loadData()
    } catch (e) {
      logger.error(e)
    }
  }

  onToggleActivity = () => {
    this.handleOnToggle(!this.isShowActivity, 'conversation', 'show_activity')
  }

  onToggleHideCalls = () => {
    this.handleOnToggle(!this.isHideCalls, 'conversation', 'hide_calls')
  }

  onToggleUndoMessage = () => {
    this._isClickToUndo = !this._isClickToUndo

    this._entities.userSettingsStore.updateSetting({
      value: this._isClickToUndo,
      featureKey: 'message-form',
      settingsKey: 'click_to_undo',
    })
  }

  setIsActiveTab = (value: boolean) => {
    this._isActiveTab = value
  }

  onViewing = () => {
    if (this._onViewingTimerId) clearTimeout(this._onViewingTimerId)
    if (this._canSendViewing && this._isActiveTab && !!this.conversation?.id) {
      this._entities.websocket.sendEvent(
        `private-inbox.${this.conversation?.inbox_id}.conversation`,
        'client-viewing',
        {
          id: this._entities.usersStore.user?.id,
          first_name: this._entities.usersStore.user?.first_name,
          last_name: this._entities.usersStore.user?.last_name,
          conversation_id: this.conversation?.id,
        }
      )
      this._canSendViewing = false

      this._onViewingTimerId = setTimeout(() => {
        this._canSendViewing = true
        this.onViewing()
      }, this._throttleViewingTime)
    }
  }

  handleTypingEvent = (data: IResponseEventTyping) => {
    if (data.conversation_id !== Number(this.conversationId)) return

    const timerId = setTimeout(() => {
      this._typingsMap.delete(data.id)
      this.updateViewingItemStatus(data.id, 'viewing')
    }, 2000)
    const newTyping = new Typing(data, timerId)
    const oldTyping = this._typingsMap.get(newTyping.userId)
    if (oldTyping) {
      clearTimeout(oldTyping.timerId)
    }
    this._typingsMap.set(newTyping.userId, newTyping)
    this.updateViewingItemStatus(data.id, 'typing')
  }

  handleViewingEvent = (data: IResponseEventViewing) => {
    if (data.conversation_id !== Number(this.conversationId)) return

    const timerId = setTimeout(() => {
      this._viewingsMap.delete(data.id)
    }, 35000)
    const status: IViewingStatus = this._typingsMap.get(data.id) ? 'typing' : 'viewing'

    const newViewing = new Viewing(data, timerId, status)
    const oldViewing = this._viewingsMap.get(newViewing.userId)
    if (oldViewing) {
      clearTimeout(oldViewing.timerId)
    }
    this._viewingsMap.set(newViewing.userId, newViewing)
  }

  updateViewingItemStatus = (id: number, status: IViewingStatus) => {
    const current = this._viewingsMap.get(id)
    if (current) {
      current.status = status
    }
  }

  handleContactOptInRequest = async () => {
    try {
      this.messageFieldStore.setLoading(true)

      const conversationId = this.conversationStore.currentItem?.id

      if (!conversationId) return

      const { data } = await ContactsApi.updateContactOptInRequestById(conversationId)

      this._entities.contactsStore.updateItem(data.contact)
      await this._conversationMessageFieldStore.handleRead()
    } catch (e) {
      logger.error(e)
    } finally {
      this.messageFieldStore.setLoading(false)
    }
  }

  get isScheduleMessage() {
    return this._isScheduleMessage
  }

  get disabledDoubleOptIn() {
    const number_id = this.conversation?.number_id

    return Boolean(
      this.loading ||
        !this._entities.userPermissionsStore.getItem('can_manage_contacts') ||
        this.contact?.getReceivedOptInRequestFromNumber(number_id) ||
        this.contact?.opt_out
    )
  }

  get isSmallAirCallAttach() {
    return this.messageFieldStore.isSmallAirCallAttach
  }

  setHideTrialAlertNotForOwner = () => {
    this._isHideTrialAlertNotForOwner = true
  }

  get callModalStore() {
    return this._callModalStore
  }

  get hasPrevious() {
    return this._hasPrevious
  }

  get loading() {
    return this._loading
  }

  get loadingPrevious() {
    return this._loadingPrevious
  }

  get loadingMessages() {
    return this._loadingMessages
  }

  get loadingConversation() {
    return this._loadingConversation
  }

  get messageFieldStore() {
    return this._conversationMessageFieldStore.messageFieldStore
  }

  get scrollBottomTrigger() {
    return this._scrollBottomTrigger
  }

  get mediaLoadedTrigger() {
    return this._mediaLoadedTrigger
  }

  get isShowActivity() {
    return this._entities.userSettingsStore.isUserShowActivity
  }

  get isHideCalls() {
    return this._entities.userSettingsStore.isHideCalls
  }

  get isHideTrialAlertNotForOwner() {
    return this._isHideTrialAlertNotForOwner
  }

  get isConversationNew() {
    return this._conversationId === 0
  }

  get conversationOriginId() {
    return this._conversationId
  }

  get conversationId() {
    return this._conversationId || 0
  }

  get conversation() {
    if (typeof this._conversationId !== 'number') return

    return this.conversationStore.getItem(this._conversationId)
  }

  get loadingBeforeId() {
    return this._loadingBeforeId
  }

  get messageSignatureStore() {
    return this._conversationMessageFieldStore.messageSignatureStore
  }

  get isEditMessage() {
    return this._isEditMessage
  }

  get isClickToUndo() {
    return this._isClickToUndo
  }

  get isModeNoteObj() {
    return this._isModeNoteObj
  }

  setHideAvatars = (value: boolean) => {
    this._isHideAvatars = value
  }

  onBack = () => {
    this._config?.onBack?.()
    this._pageLayoutStore?.toStartContainer()
    this.messageFieldStore.setBlurMessageFieldTrigger()
  }

  setVariant = (variant: IConversationMessagesVariant) => {
    this._variant = variant
  }

  resetVariant = () => {
    this._variant = null
  }

  get isOpenConversationVariant() {
    return this._variant === 'open_conversation'
  }

  private _sendFromStore: SendFromStore | null = null
  get sendFromStore() {
    return this._sendFromStore
  }
  setSendFromStore = (sendFromStore: SendFromStore | null) => {
    this._sendFromStore = sendFromStore
  }

  private _quietHoursClosureStore: QuietHoursClosureStore | null = null
  get quietHoursClosureStore() {
    if (!this._quietHoursClosureStore) {
      this._quietHoursClosureStore = new QuietHoursClosureStore()
    }
    return this._quietHoursClosureStore
  }

  get quietHours() {
    return new QuietHours(this._entities.organizationStore.quietHoursSettings)
  }

  get isQuietHoursEnabled() {
    return this.quietHours.isQuietHoursEnabled
  }

  get hasUserConfirmedQuietHoursForCurrentContact(): boolean {
    if (this._contactId === null || this._inboxId === null) return false

    return this._entities.quietHoursSendAnywayStore.hasKey(this._contactId, this._inboxId)
  }

  get isShowQuietHoursModal() {
    return (
      !this._isModeNoteObj.value &&
      this.isQuietHoursEnabled &&
      !this.hasUserConfirmedQuietHoursForCurrentContact &&
      isInQuietHoursTimeframe(
        this.getContactTZ(),
        this.quietHours.quietHoursAllowedTimeFrom,
        this.quietHours.quietHoursAllowedTimeTo
      )
    )
  }

  get messageIdQuery() {
    const search = window.location.search
    const query = new URLSearchParams(search)

    return stringToNumber(query.get('messageId'))
  }

  showQuietHoursModal = ({ scheduledData }: { scheduledData?: IScheduledData }) => {
    const id = 'quiet_hours_modal'
    const contactName = this.contact?.first_name || this.contact?.last_name || 'this contact'
    const localContactTime = getLocalContactTimeFormatted(this.getContactTZ())
    const contactTZ = this.getContactTZ()

    const sendAt =
      getNextQuietHoursSendAt({
        contactTZ,
        allowed_time_from: this.quietHours.quietHoursAllowedTimeFrom,
        allowed_time_to: this.quietHours.quietHoursAllowedTimeTo,
        is_block_on_weekend: this.quietHours.isQuietHoursBlockOnWeekend,
      }) ?? dayjs().tz(contactTZ).add(1, 'hour').startOf('hour')
    const scheduleText = getScheduleText({ tz: contactTZ, send_at: sendAt, scheduleFor: true })

    const handleConfirm = () => {
      modalStore.removeModal(id)

      const contactId = this._contactId
      const inboxId = this._inboxId
      const onSendSuccess = () => {
        if (contactId !== null && inboxId !== null) {
          this._entities.quietHoursSendAnywayStore.addKey(contactId, inboxId)
        }
      }

      this.handleSend({ scheduledData, onSendSuccess })
    }

    const handleCancel = () => {
      modalStore.removeModal(id)
    }

    const handleSchedule = () => {
      modalStore.removeModal(id)

      const scheduledData: IScheduledData = {
        is_contact_timezone: true,
        stop_on_response: false,
        send_at: sendAt.tz(contactTZ, true).utc().format(DayjsFormats.apiFormat),
      }
      this.handleSend({ scheduledData })
    }

    modalStore.addModal({
      disabledOnAllClose: true,
      type: ModalTypeList.WARNING,
      title: 'Send message during quiet hours?',
      id,
      desc: (
        <>
          It’s currently {localContactTime} for {contactName}. We recommend scheduling your message
          for the next available sending time.
        </>
      ),
      primaryAction: {
        text: 'Yes, send anyway',
        onAction: handleConfirm,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: handleCancel,
      },
      additionalSecondaryAction: {
        text: scheduleText,
        onAction: handleSchedule,
      },
    })
  }
}
